import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ALL_ROUTES } from "../data/siteData";
import { PROYECTOS } from "../data/proyectos";

// Command palette ⌘K / Ctrl+K — easter egg funcional estilo dev-tool.
// No tiene botón visible: quien lo conoce, lo usa. Navega por todas las rutas
// públicas y los case studies.
export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const { t } = useTranslation();
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setQ("");
      // focus tras el render del overlay
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  const items = useMemo(
    () => [
      ...ALL_ROUTES.map((r) => ({ path: r.path, label: t(r.labelKey) })),
      { path: "/domina-claude", label: "Domina Claude" },
      ...PROYECTOS.map((p) => ({
        path: `/proyectos/${p.slug}`,
        label: t(`proyectos.items.${p.key}.name`),
      })),
    ],
    [t]
  );

  const filtered = items.filter((i) =>
    `${i.label} ${i.path}`.toLowerCase().includes(q.toLowerCase())
  );

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("palette.ariaLabel")}
      onClick={(e) => e.target === e.currentTarget && setOpen(false)}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        background: "rgba(3,4,9,0.7)",
        backdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "16vh",
      }}
    >
      <div
        style={{
          width: "min(600px, 92vw)",
          borderRadius: 16,
          border: "1px solid rgba(92,224,190,0.25)",
          background: "rgba(10,14,25,0.97)",
          overflow: "hidden",
          boxShadow: "0 40px 120px rgba(0,0,0,0.7)",
        }}
      >
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && filtered.length > 0) go(filtered[0].path);
          }}
          placeholder={t("palette.placeholder")}
          style={{
            width: "100%",
            background: "transparent",
            border: "none",
            outline: "none",
            color: "var(--color-text)",
            fontFamily: "var(--font-body)",
            fontSize: 15,
            padding: "18px 22px",
            borderBottom: "1px solid var(--accent-08)",
            boxSizing: "border-box",
          }}
        />
        <div style={{ padding: 10, maxHeight: "46vh", overflowY: "auto" }}>
          {filtered.length === 0 && (
            <div
              style={{
                padding: "14px",
                fontFamily: "var(--font-body)",
                fontSize: 13,
                color: "var(--text-faint)",
              }}
            >
              {t("palette.empty")}
            </div>
          )}
          {filtered.map((i) => (
            <button
              key={i.path}
              onClick={() => go(i.path)}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                padding: "12px 14px",
                borderRadius: 10,
                border: "none",
                background: "transparent",
                cursor: "pointer",
                fontFamily: "var(--font-body)",
                fontSize: 14,
                color: "var(--text-muted)",
                textAlign: "left",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(92,224,190,0.07)";
                e.currentTarget.style.color = "var(--color-text)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = "var(--text-muted)";
              }}
            >
              <span>▸ {i.label}</span>
              <span
                style={{
                  fontFamily: "var(--font-body)",
                  fontSize: 10.5,
                  color: "var(--text-faint)",
                  border: "1px solid var(--accent-08)",
                  padding: "2px 8px",
                  borderRadius: 5,
                }}
              >
                {i.path}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
